import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import RouteMediaManager from "@/components/admin/RouteMediaManager";

const LANGS = ["en", "es", "ru"];
const blank = {
  slug: { en: "", es: "", ru: "" },
  title: { en: "", es: "", ru: "" },
  seoTitle: { en: "", es: "", ru: "" },
  metaDescription: { en: "", es: "", ru: "" },
  h1: { en: "", es: "", ru: "" },
  shortDescription: { en: "", es: "", ru: "" },
  stops: { en: [], es: [], ru: [] },
  recommendedCarType: { en: "", es: "", ru: "" },
  ctaText: { en: "", es: "", ru: "" },
  seoText: { en: "", es: "", ru: "" },
  routeMedia: [],
  season: "", active: true,
  // Prompt 10
  whyPremium: { en: "", es: "", ru: "" },
  bestTime: { en: "", es: "", ru: "" },
  durationText: { en: "", es: "", ru: "" },
  serviceAreas: [],
  seoDescriptionLong: { en: "", es: "", ru: "" },
  faqs: [],
  seoEnabled: true,
  noindex: false,
};

export default function RoutesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [activeLang, setActiveLang] = useState("en");

  const load = async () => {
    const { data } = await api.get("/routes?only_active=false");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (editing.id) await api.put(`/routes/${editing.id}`, editing);
    else await api.post("/routes", editing);
    setEditing(null); load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/routes/${id}`); load();
  };

  const startEdit = (r) => {
    const merged = { ...JSON.parse(JSON.stringify(blank)) };
    ["slug","title","seoTitle","metaDescription","h1","shortDescription","recommendedCarType","ctaText","seoText","whyPremium","bestTime","durationText","seoDescriptionLong"].forEach((k) => {
      merged[k] = { ...blank[k], ...(r[k] || {}) };
    });
    merged.stops = { en: r.stops?.en || [], es: r.stops?.es || [], ru: r.stops?.ru || [] };
    merged.serviceAreas = Array.isArray(r.serviceAreas) ? r.serviceAreas : [];
    merged.faqs = (Array.isArray(r.faqs) ? r.faqs : []).map((f) => ({
      _uid: f._uid || `faq-${Math.random().toString(36).slice(2, 9)}`,
      ...f,
    }));
    merged.seoEnabled = r.seoEnabled !== false;
    merged.noindex = !!r.noindex;
    merged.routeMedia = r.routeMedia || (Array.isArray(r.images) ? r.images.map((u, i) => ({
      type: "image", url: typeof u === "string" ? u : u.url, posterUrl: "",
      alt: { en: "", es: "", ru: "" }, title: { en: "", es: "", ru: "" },
      isMain: i === 0, order: i,
    })) : []);
    merged.season = r.season || "";
    merged.active = r.active !== false;
    merged.id = r.id;
    setEditing(merged);
  };

  const addFaq = () =>
    setEditing((prev) => ({
      ...prev,
      faqs: [
        ...(prev.faqs || []),
        { _uid: `faq-${Math.random().toString(36).slice(2, 9)}`, q: { en: "", es: "", ru: "" }, a: { en: "", es: "", ru: "" } },
      ],
    }));
  const removeFaq = (i) =>
    setEditing((prev) => ({ ...prev, faqs: (prev.faqs || []).filter((_, idx) => idx !== i) }));
  const setFaq = (i, key, lang, value) =>
    setEditing((prev) => ({
      ...prev,
      faqs: (prev.faqs || []).map((f, idx) => (idx === i ? { ...f, [key]: { ...(f[key] || {}), [lang]: value } } : f)),
    }));

  const previewImg = (r) => {
    const media = r.routeMedia || [];
    const m = media.find((x) => x.isMain) || media[0];
    if (m) return m.posterUrl || m.url;
    if (Array.isArray(r.images) && r.images.length) return typeof r.images[0] === "string" ? r.images[0] : r.images[0].url;
    return "";
  };

  return (
    <div data-testid="admin-routes-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-[#F5F1EB] text-3xl">Routes</h1>
        <button className="btn-gold" data-testid="admin-route-new" onClick={() => setEditing({ ...JSON.parse(JSON.stringify(blank)) })}>
          <Plus size={14}/> New
        </button>
      </div>

      <div className="grid gap-3">
        {items.map((r) => (
          <div key={r.id} className="border border-[#2A2A2A] p-4 flex gap-4 items-center" data-testid={`admin-route-row-${r.id}`}>
            <img src={previewImg(r)} alt="" className="w-20 h-14 object-cover bg-[#1a1a1a]" />
            <div className="flex-1 min-w-0">
              <div className="font-display text-[#F5F1EB] truncate">{r.title?.en}</div>
              <div className="text-xs text-[#8A8A8A]">/{r.slug?.en} · {r.active ? "Active" : "Hidden"}</div>
            </div>
            <button onClick={() => startEdit(r)} className="p-2 border border-[#2A2A2A] hover:border-[#C9A55A]" data-testid={`admin-route-edit-${r.id}`}><Pencil size={14}/></button>
            <button onClick={() => remove(r.id)} className="p-2 border border-[#2A2A2A] hover:border-[#B8342A] hover:text-[#B8342A]" data-testid={`admin-route-delete-${r.id}`}><Trash2 size={14}/></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-[#0C0C0C]/95 z-50 overflow-y-auto" data-testid="admin-route-editor">
          <div className="max-w-3xl mx-auto p-6 md:p-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-[#F5F1EB] text-2xl">{editing.id ? "Edit" : "New"} route</h2>
              <button onClick={() => setEditing(null)} className="p-2 border border-[#2A2A2A] text-[#F5F1EB]"><X size={16}/></button>
            </div>

            <Field label="Season" value={editing.season} onChange={(v) => setEditing({...editing, season: v})} />

            <div className="mt-6">
              <div className="text-overline text-[#C9A55A] mb-3">Media · images + videos · reorder · set main</div>
              <RouteMediaManager media={editing.routeMedia} onChange={(routeMedia) => setEditing({...editing, routeMedia})} />
            </div>

            <div className="mt-6 border border-[#2A2A2A]">
              <div className="flex border-b border-[#2A2A2A]">
                {LANGS.map((l) => (
                  <button key={l} type="button"
                    onClick={() => setActiveLang(l)}
                    className={`px-4 py-2 text-xs tracking-[0.18em] uppercase font-semibold ${activeLang === l ? "bg-[#C9A55A] text-[#0C0C0C]" : "text-[#F5F1EB]"}`}>
                    {l}
                  </button>
                ))}
              </div>
              <div className="p-4 space-y-3">
                <Field label={`Slug (${activeLang})`} value={editing.slug[activeLang]}
                  onChange={(v) => setEditing({...editing, slug: {...editing.slug, [activeLang]: v.toLowerCase().replace(/\s+/g, "-")}})} />
                <Field label={`Title (${activeLang})`} value={editing.title[activeLang]}
                  onChange={(v) => setEditing({...editing, title: {...editing.title, [activeLang]: v}})} />
                <Field label={`H1 (${activeLang})`} value={editing.h1[activeLang]}
                  onChange={(v) => setEditing({...editing, h1: {...editing.h1, [activeLang]: v}})} />
                <Field label={`SEO Title (${activeLang})`} value={editing.seoTitle[activeLang]}
                  onChange={(v) => setEditing({...editing, seoTitle: {...editing.seoTitle, [activeLang]: v}})} />
                <Field label={`Meta description (${activeLang})`} value={editing.metaDescription[activeLang]}
                  onChange={(v) => setEditing({...editing, metaDescription: {...editing.metaDescription, [activeLang]: v}})} textarea />
                <Field label={`Short description (${activeLang})`} value={editing.shortDescription[activeLang]}
                  onChange={(v) => setEditing({...editing, shortDescription: {...editing.shortDescription, [activeLang]: v}})} textarea />
                <Field label={`Stops (${activeLang}) — one per line`} value={(editing.stops[activeLang] || []).join("\n")}
                  onChange={(v) => setEditing({...editing, stops: {...editing.stops, [activeLang]: v.split("\n").map(s => s.trim()).filter(Boolean)}})} textarea />
                <Field label={`Recommended car (${activeLang})`} value={editing.recommendedCarType[activeLang]}
                  onChange={(v) => setEditing({...editing, recommendedCarType: {...editing.recommendedCarType, [activeLang]: v}})} />
                <Field label={`Why this route is premium (${activeLang})`} value={editing.whyPremium[activeLang]}
                  onChange={(v) => setEditing({...editing, whyPremium: {...editing.whyPremium, [activeLang]: v}})} textarea testid={`r-whyPremium-${activeLang}`} />
                <Field label={`Best time of day (${activeLang})`} value={editing.bestTime[activeLang]}
                  onChange={(v) => setEditing({...editing, bestTime: {...editing.bestTime, [activeLang]: v}})} testid={`r-bestTime-${activeLang}`} />
                <Field label={`Approximate duration (${activeLang})`} value={editing.durationText[activeLang]}
                  onChange={(v) => setEditing({...editing, durationText: {...editing.durationText, [activeLang]: v}})} testid={`r-durationText-${activeLang}`} />
                <Field label={`SEO long copy (${activeLang})`} value={editing.seoDescriptionLong[activeLang]}
                  onChange={(v) => setEditing({...editing, seoDescriptionLong: {...editing.seoDescriptionLong, [activeLang]: v}})} textarea testid={`r-seoLong-${activeLang}`} />
                <Field label={`CTA text (${activeLang})`} value={editing.ctaText[activeLang]}
                  onChange={(v) => setEditing({...editing, ctaText: {...editing.ctaText, [activeLang]: v}})} />
                <Field label={`SEO text (${activeLang})`} value={editing.seoText[activeLang]}
                  onChange={(v) => setEditing({...editing, seoText: {...editing.seoText, [activeLang]: v}})} textarea />
              </div>
            </div>

            <div className="mt-6 border border-[#2A2A2A] p-4" data-testid="r-seo-block">
              <div className="text-overline text-[#C9A55A] mb-3">SEO & service areas</div>
              <Field
                label="Service areas (comma-separated, e.g. Benidorm, Altea, Calpe)"
                value={(editing.serviceAreas || []).join(", ")}
                onChange={(v) => setEditing({...editing, serviceAreas: v.split(",").map((x) => x.trim()).filter(Boolean)})}
                testid="r-serviceAreas"
              />
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <label className="flex items-center gap-2 text-[#F5F1EB]">
                  <input type="checkbox" checked={editing.seoEnabled !== false} onChange={(e) => setEditing({...editing, seoEnabled: e.target.checked})} data-testid="r-seoEnabled" />
                  SEO page enabled
                </label>
                <label className="flex items-center gap-2 text-[#F5F1EB]">
                  <input type="checkbox" checked={!!editing.noindex} onChange={(e) => setEditing({...editing, noindex: e.target.checked})} data-testid="r-noindex" />
                  noindex (hide from search engines)
                </label>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-overline text-[#C9A55A]">FAQs</div>
                  <button type="button" onClick={addFaq} className="text-xs uppercase tracking-[0.18em] text-[#C9A55A] hover:text-[#F5F1EB]" data-testid="r-faq-add">+ Add FAQ</button>
                </div>
                {(editing.faqs || []).length === 0 && <p className="text-[10px] text-[#8A8A8A]">No FAQs yet.</p>}
                {(editing.faqs || []).map((f, i) => (
                  <div key={f._uid || `faq-${i}`} className="border border-[#2A2A2A] p-3 mb-3" data-testid={`r-faq-item-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] text-[#8A8A8A] tracking-[0.18em] uppercase">FAQ #{i + 1}</div>
                      <button type="button" onClick={() => removeFaq(i)} className="text-[10px] text-[#B8342A] uppercase tracking-[0.18em]" data-testid={`r-faq-del-${i}`}>Remove</button>
                    </div>
                    <Field label={`Question (${activeLang})`} value={f.q?.[activeLang] || ""} onChange={(v) => setFaq(i, "q", activeLang, v)} testid={`r-faq-q-${i}-${activeLang}`} />
                    <div className="mt-2">
                      <Field label={`Answer (${activeLang})`} value={f.a?.[activeLang] || ""} onChange={(v) => setFaq(i, "a", activeLang, v)} textarea testid={`r-faq-a-${i}-${activeLang}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex items-center gap-6 text-sm">
              <label className="flex items-center gap-2 text-[#F5F1EB]"><input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({...editing, active: e.target.checked})} /> Active</label>
            </div>

            <div className="mt-8 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="px-5 py-3 border border-[#2A2A2A] text-[#F5F1EB] uppercase text-xs tracking-[0.18em]">Cancel</button>
              <button onClick={save} className="btn-gold" data-testid="route-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Label = ({ children }) => <label className="text-[10px] text-[#8A8A8A] tracking-[0.18em] uppercase block mb-1">{children}</label>;
function Field({ label, value, onChange, textarea = false, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      {textarea ? (
        <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 py-2 text-sm" />
      ) : (
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm" />
      )}
    </div>
  );
}
